import fetchJson from "@/lib/fetchJson";
import useSWR, { type SWRConfiguration } from "swr";
import { isValidId } from "@/lib/util";

export const useEngagements = (campaignId: string | number | undefined) =>
  useSWR(
    isValidId(String(campaignId)) ? `/engagement/campaign/${campaignId}` : null,
    fetchJson
  );

export type EngagementAuditFilters = {
  creatorId?: number;
  action?: string;
  startDate?: string;
  endDate?: string;
};

export const useEngagementAuditLog = (
  campaignId: string | number | undefined,
  filters?: EngagementAuditFilters,
  enabled = true
) => {
  const qs = new URLSearchParams();
  if (filters?.creatorId) qs.set("creatorId", String(filters.creatorId));
  if (filters?.action) qs.set("action", filters.action);
  if (filters?.startDate) qs.set("startDate", filters.startDate);
  if (filters?.endDate) qs.set("endDate", filters.endDate);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return useSWR(
    enabled && isValidId(String(campaignId))
      ? `/engagement/campaign/${campaignId}/audit${suffix}`
      : null,
    fetchJson
  );
};

export type OutreachChannel = "Sms" | "Instagram";

export type IgTargetInput = {
  instagramAccountId: string;
  recipientIgScopedId: string;
  instagramConversationId?: string;
};

export type CreateEngagementsOptions = {
  notes?: string;
  customPrices?: Record<number, number>;
  outreachChannel?: OutreachChannel;
  /** Single-creator Instagram target (when creatorPriceIds.length === 1). */
  instagramAccountId?: string;
  recipientIgScopedId?: string;
  instagramConversationId?: string;
  /** Per-creator Instagram targets when adding multiple creators. */
  igTargets?: Record<number, IgTargetInput>;
};

export const createEngagements = async (
  campaignId: number,
  creatorPriceIds: number[],
  options?: CreateEngagementsOptions
) =>
  fetchJson("/engagement/", {
    method: "POST",
    body: JSON.stringify({
      campaignId,
      creatorPriceIds,
      notes: options?.notes,
      customPrices: options?.customPrices,
      outreachChannel: options?.outreachChannel,
      instagramAccountId: options?.instagramAccountId,
      recipientIgScopedId: options?.recipientIgScopedId,
      instagramConversationId: options?.instagramConversationId,
      igTargets: options?.igTargets,
    }),
  });

export const patchEngagement = async (
  id: string,
  body: { customPrice?: number | null; notes?: string }
) =>
  fetchJson(`/engagement/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export type TransitionStateBody = {
  declineReason?: string;
  /** When `false`, skip the automatic state-transition SMS entirely. */
  sendStateSms?: boolean;
  /** When set (non-empty after trim), used as the SMS body (server still resolves `{{…}}`). */
  stateSmsBody?: string;
};

export const transitionState = async (
  id: string,
  state: string,
  options?: string | TransitionStateBody
) => {
  const body: TransitionStateBody =
    typeof options === "string" ? { declineReason: options } : options ?? {};
  return fetchJson(`/engagement/${id}/state`, {
    method: "POST",
    body: JSON.stringify({ state, ...body }),
  });
};

export const sendEngagementMessage = async (
  id: string,
  options: { body?: string; templateId?: string }
) =>
  fetchJson(`/engagement/${id}/message`, {
    method: "POST",
    body: JSON.stringify(options),
  });

export const sendBulkMessage = async (options: {
  engagementIds?: string[];
  campaignId?: number;
  stateFilter?: string;
  templateId?: string;
  customBody?: string;
}) =>
  fetchJson("/engagement/bulk-message", {
    method: "POST",
    body: JSON.stringify(options),
  });

export const deleteEngagement = async (id: string) =>
  fetchJson(`/engagement/${id}`, {
    method: "DELETE",
  });

export type SmsEngagementMessage = {
  kind: "sms";
  id: string;
  createdAt: string;
  direction: string;
  body: string;
  status: string;
  provider: string | null;
  sentBy: string | null;
  errorMessage: string | null;
  templateId: string | null;
};

export type InstagramEngagementMessage = {
  kind: "instagram";
  id: string;
  createdAt: string;
  direction: string;
  body: string | null;
  status: string;
  sentBy: string | null;
  errorMessage: string | null;
  isHumanAgentReply: boolean;
  mid: string;
};

export type EngagementMessage = SmsEngagementMessage | InstagramEngagementMessage;

export const useEngagementMessages = (
  engagementId: string | undefined,
  swrOptions?: SWRConfiguration
) =>
  useSWR<EngagementMessage[]>(
    engagementId ? `/engagement/${engagementId}/messages` : null,
    fetchJson,
    swrOptions
  );

export type InstagramRecipientMatch = {
  id: string;
  igAccountId: string;
  participantIgScopedId: string;
  participantUsername: string | null;
  lastInboundAt: string | null;
  lastMessageAt: string | null;
};

export type InstagramRecipientsResponse = {
  accounts: Array<{ id: string; igUserId: string; igUsername: string | null }>;
  matches: InstagramRecipientMatch[];
  creatorUsername: string;
};

export const useInstagramRecipients = (
  creatorPriceId: number | undefined,
  enabled = true
) =>
  useSWR<InstagramRecipientsResponse>(
    enabled && creatorPriceId
      ? `/engagement/instagram/recipients?creatorPriceId=${creatorPriceId}`
      : null,
    fetchJson
  );

export const updateEngagementChannel = async (
  id: string,
  body: {
    outreachChannel: OutreachChannel;
    instagramAccountId?: string;
    recipientIgScopedId?: string;
    instagramConversationId?: string;
  }
) =>
  fetchJson(`/engagement/${id}/channel`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const useSmsTemplates = (enabled = true) =>
  useSWR(enabled ? "/sms-template/" : null, fetchJson);

export const createSmsTemplate = async (data: {
  name: string;
  triggerState?: string;
  body: string;
  enabled?: boolean;
}) =>
  fetchJson("/sms-template/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateSmsTemplate = async (
  id: string,
  data: { name?: string; triggerState?: string; body?: string; enabled?: boolean }
) =>
  fetchJson(`/sms-template/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const deleteSmsTemplate = async (id: string) =>
  fetchJson(`/sms-template/${id}`, {
    method: "DELETE",
  });
